import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import artistApi from '@/lib/artistApi';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 7000;

const toEpoch = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const isIncomingForRole = (message, recipientRole) => {
  const senderType = String(message?.sender_type || '')
    .trim()
    .toLowerCase();
  if (!senderType) return true;
  if (senderType === recipientRole) return false;
  return true;
};

const buildMessageToken = (conversation, message, index) =>
  `${message?.id || ''}|${message?.created_at || ''}|${conversation?.id || ''}|${index}`;

const isPmbInboxPath = (pathname) => {
  const normalized = String(pathname || '').replace(/\/+$/, '');
  return normalized === '/artist/inbox' || normalized === '/collaborator/inbox';
};

const incomingMessageFromConversations = (conversations, recipientRole) => {
  let latest = null;
  for (const conversation of conversations || []) {
    const messages = Array.isArray(conversation?.messages) ? conversation.messages : [];
    messages.forEach((message, index) => {
      if (!isIncomingForRole(message, recipientRole)) return;
      const epoch = toEpoch(message?.created_at);
      const token = buildMessageToken(conversation, message, index);
      if (!latest || epoch > latest.epoch || (epoch === latest.epoch && token !== latest.token)) {
        latest = {
          epoch,
          token,
          senderName: message?.sender_name || conversation?.artist_name || conversation?.collaborator_name || 'KalaManch User',
          text: message?.message || 'You have a new PMB message.',
        };
      }
    });
  }
  return latest;
};

const PmbSessionNotifier = () => {
  const { artist, isAuthenticated: artistLoggedIn } = useArtistAuth();
  const { collaborator, isAuthenticated: collaboratorLoggedIn } = useCollaboratorAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [popup, setPopup] = useState(null);
  const initializedRef = useRef(false);

  const activeRole = useMemo(() => {
    if (artistLoggedIn && artist?.id) return 'artist';
    if (collaboratorLoggedIn && collaborator?.id) return 'collaborator';
    return '';
  }, [artistLoggedIn, artist?.id, collaboratorLoggedIn, collaborator?.id]);

  const activeUserId = activeRole === 'artist' ? artist?.id : collaborator?.id;
  const inboxRoute = activeRole === 'artist' ? '/artist/inbox' : '/collaborator/inbox';
  const storageKey = `kalamanch_pmb_seen_${activeRole}_${activeUserId || 'anon'}`;
  const isOnPmbInbox = isPmbInboxPath(location.pathname);

  useEffect(() => {
    if (!isOnPmbInbox) return;
    setPopup(null);
    window.dispatchEvent(
      new CustomEvent('kalamanch:pmb-refresh-request', {
        detail: {
          role: activeRole,
          source: 'session-notifier-route',
        },
      })
    );
  }, [activeRole, isOnPmbInbox]);

  useEffect(() => {
    if (!activeRole || !activeUserId) {
      setPopup(null);
      initializedRef.current = false;
      return undefined;
    }

    let alive = true;
    const readSeenToken = () => sessionStorage.getItem(storageKey) || '';
    const writeSeenToken = (token) => {
      if (!token) return;
      sessionStorage.setItem(storageKey, token);
    };

    const loadConversations = async () => {
      try {
        const response =
          activeRole === 'artist'
            ? await artistApi.get('/artist-auth/enquiries')
            : await axios.get(`${API}/collaborator-auth/enquiries`, { withCredentials: true });
        if (!alive) return;
        const latestIncoming = incomingMessageFromConversations(response.data?.conversations || [], activeRole);
        if (!latestIncoming?.token) return;

        const seenToken = readSeenToken();
        if (!initializedRef.current) {
          initializedRef.current = true;
          if (!seenToken) {
            writeSeenToken(latestIncoming.token);
            return;
          }
        }
        if (latestIncoming.token === seenToken) return;
        if (isPmbInboxPath(window.location.pathname)) {
          writeSeenToken(latestIncoming.token);
          setPopup(null);
          window.dispatchEvent(
            new CustomEvent('kalamanch:pmb-refresh-request', {
              detail: {
                role: activeRole,
                token: latestIncoming.token,
                source: 'session-notifier',
              },
            })
          );
          return;
        }
        setPopup(latestIncoming);
      } catch (_error) {
      }
    };

    loadConversations();
    const interval = setInterval(loadConversations, POLL_INTERVAL_MS);

    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [activeRole, activeUserId, storageKey, isOnPmbInbox]);

  if (!popup || !activeRole || !activeUserId) return null;

  const acknowledge = () => {
    sessionStorage.setItem(storageKey, popup.token);
    setPopup(null);
  };

  const openPmb = () => {
    acknowledge();
    if (location.pathname !== inboxRoute) {
      navigate(inboxRoute);
    }
    window.dispatchEvent(new CustomEvent('kalamanch:pmb-refresh-request'));
  };

  return (
    <div className="fixed right-4 top-24 z-[120] w-[min(92vw,360px)] rounded-xl border border-orange-200 bg-white/95 p-4 shadow-xl backdrop-blur">
      <p className="text-[11px] uppercase tracking-[0.2em] text-orange-600 font-semibold">New PMB message</p>
      <p className="mt-1 text-sm font-semibold text-gray-900">{popup.senderName}</p>
      <p className="mt-1 text-sm text-gray-700 line-clamp-3">{popup.text}</p>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={acknowledge}
          className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100"
        >
          Dismiss
        </button>
        <button
          type="button"
          onClick={openPmb}
          className="rounded-full border border-orange-300 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 hover:bg-orange-100"
        >
          Open PMB
        </button>
      </div>
    </div>
  );
};

export default PmbSessionNotifier;
